import { useEffect, useRef, useState, useCallback } from 'react';
import { Api } from './api';
import { toQuery } from './utils';

interface UseRequestConfig<T> {
  useCursor?: boolean;
  skipInitialCursor?: boolean;
  useOffset?: boolean;
  limit?: number;
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
  payload?: Record<string, any>;
  transform?: (result: T) => T;
  language?: string;
  enabled?: boolean;
}

export function useRequest<T extends { data: any[], meta: any }>(
  endpoint: string,
  config: UseRequestConfig<T> = {}
) {
  const {
    onSuccess,
    onError,
    limit,
    payload = {},
    language,
    enabled = true,
    skipInitialCursor = false,
  } = config;
  const [data, setData] = useState<T['data']>([]);
  const [meta, setMeta] = useState<T['meta']>({});
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const offset = useRef<number | null>(null);
  const cursor = useRef<string | boolean>(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasFetchedRef = useRef(false);

  const fetchData = useCallback(async (reset = false) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const params = {
        ...(config.useOffset ? { offset: offset.current !== null ? offset.current + (limit || 0) : 0 } : {}),
        // Only send cursor if it's a string, or on initial request unless skipInitialCursor is set
        // eslint-disable-next-line no-nested-ternary
        ...(config.useCursor &&
          (typeof cursor.current === 'string' || (cursor.current === true && !skipInitialCursor))
           ? { cursor: cursor.current } : {}),
        ...(config.limit ? { limit } : {}),
        ...payload,
      };

      const query = toQuery(params);
      const rawResult = await Api.get<T>(`${endpoint}?${query}`, true, {
        signal: abortControllerRef.current?.signal,
        language,
      });

      const result = config.transform ? config.transform(rawResult) : rawResult;

      if (abortControllerRef.current?.signal.aborted) return;

      switch (true) {
        case config.useOffset:
          setData((prev) => (reset ? result.data : [...prev, ...result.data]));
          setMeta(result.meta);
          // Always track the current offset from the response for proper pagination
          offset.current = result.meta.pagination?.offset ?? 0;
          break;
        case config.useCursor:
          setData((prev) => (reset ? result.data : [...prev, ...result.data]));
          setMeta(result.meta);
          cursor.current = result.meta.pagination?.next_cursor || true;
          break;
        default:
          setData(result.data);
          setMeta(result.meta);
          break;
      }
      setLoading(false);
      onSuccess?.(result);
    } catch (err: any) {
      if (err.name === 'AbortError') {
        return;
      }
      const newErr = err instanceof Error ? err : new Error(String(err));
      setError(newErr);
      onError?.(newErr);
      setLoading(false);
    }
  }, [endpoint, limit, payload, onSuccess, onError, language]);

  useEffect(() => {
    if (enabled && !hasFetchedRef.current) {
      fetchData();
      hasFetchedRef.current = true;
    } else if (!enabled) {
      // Reset the hasFetchedRef when disabled so it can fetch when enabled again
      hasFetchedRef.current = false;
    }
  }, [enabled, fetchData]);

  useEffect(() => () => {
    abortControllerRef.current?.abort();
  }, []);

  const reset = useCallback((options?: { skipFetch?: boolean }) => {
    if (!enabled) return;
    setData([]);
    cursor.current = true;
    offset.current = null;
    if (!options?.skipFetch) {
      fetchData(true);
    }
  }, [enabled, fetchData]);

  const syncCursorAndMeta = useCallback((nextCursor: string | boolean, nextMeta: T['meta']) => {
    cursor.current = nextCursor;
    setMeta(nextMeta);
  }, []);

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    setLoading(false);
  }, []);

  return {
    data,
    setData,
    meta,
    error,
    loading,
    refetch: async () => {
      if (!enabled) return Promise.resolve();
      await fetchData(false);
    },
    reset,
    abort,
    syncCursorAndMeta,
  };
}
